import { useState, useEffect } from "react";
import {
  Save,
  Edit2,
  Loader2,
  AlertCircle,
  Plus,
  FileText,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Alert, AlertDescription } from "../../../components/ui/alert";
import { toast } from "../../../utils/toast";
import {
  LETTER_TYPES,
  DEFAULT_TEMPLATES,
  getPlaceholders,
} from "../../../utils/letterTemplates";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../../api/api";
import { LetterLibrary } from "./LetterLibrary";
import "./letterTemplates.css";

const LetterTemplates = () => {
  const [activeTab, setActiveTab] = useState("templates");
  const [templates, setTemplates] = useState({});
  const [templateIds, setTemplateIds] = useState({});
  const [selectedType, setSelectedType] = useState("");
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(false);

  const navigate = useNavigate();
  const role = localStorage.getItem("role");
  const userData = JSON.parse(localStorage.getItem("userdata") || "{}");

  /* ── Load templates once on mount ── */
  useEffect(() => {
    fetchTemplates();
  }, []);

  /* ── Load content only when the selected TYPE changes, not on every templates update.
       This prevents the "revert" bug: after a save we update `templates` locally which
       would re-trigger loadTemplate and overwrite the just-saved values. ── */
  useEffect(() => {
    if (selectedType) loadTemplate(selectedType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedType]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/settings/letter-templates`,
      );
      const dbRows = Array.isArray(data) ? data : [];
      const tMap = {},
        idMap = {};
      dbRows.forEach((t) => {
        tMap[t.document_type] = { subject: t.subject, content: t.body };
        idMap[t.document_type] = t.id;
      });
      setTemplates({ ...DEFAULT_TEMPLATES, ...tMap });
      setTemplateIds(idMap);
    } catch (err) {
      console.error("Error fetching templates:", err);
      setTemplates(DEFAULT_TEMPLATES);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplate = (type) => {
    const merged = { ...DEFAULT_TEMPLATES, ...templates };
    const tpl = merged[type];
    if (tpl) {
      setSubject(tpl.subject || "");
      setContent(tpl.content || tpl.body || "");
      setIsEditing(false);
    }
  };

  const handleSave = async () => {
    if (!selectedType) return toast.error("Please select a letter type");
    if (!subject) return toast.error("Subject is required");
    if (!content) return toast.error("Content is required");

    setSaving(true);
    try {
      const existingId = templateIds[selectedType];
      if (existingId) {
        await axios.put(
          `${API_URL}/api/settings/letter-templates/${existingId}`,
          {
            subject,
            body: content,
            updated_by: userData.id,
          },
        );
      } else {
        const response = await axios.post(
          `${API_URL}/api/settings/letter-templates`,
          {
            template_name:
              LETTER_TYPES.find((t) => t.value === selectedType)?.label ||
              selectedType,
            document_type: selectedType,
            subject,
            body: content,
            created_by: userData.id,
          },
        );
        // Store new ID so future edits use PUT, not POST
        const newId = response.data?.id;
        if (newId) {
          setTemplateIds((prev) => ({ ...prev, [selectedType]: newId }));
        }
      }

      /* ── Optimistic local update ─────────────────────────────────────────
         Update templates state directly so the displayed content stays at
         the newly saved values. We do NOT re-fetch from DB here because
         that would trigger the selectedType useEffect → loadTemplate which
         would overwrite the editor with what the DB returns (potentially
         stale if the JOIN excludes the row). ── */
      setTemplates((prev) => ({
        ...prev,
        [selectedType]: { subject, content },
      }));
      setIsEditing(false);
      toast.success("Template saved successfully!");
    } catch (err) {
      console.error("Error saving template:", err);
      toast.error("Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    const def = DEFAULT_TEMPLATES[selectedType];
    if (def) {
      setSubject(def.subject);
      setContent(def.content);
      setIsEditing(true);
      toast.info("Reset to default — click Save Template to apply");
    }
  };

  const insertPlaceholder = (p) => {
    setContent((prev) => prev + p);
    if (!isEditing) setIsEditing(true);
  };

  return (
    <div className="lt-page">
      {/* ── Header ── */}
      <div className="lt-page-header">
        <div>
          <h2 className="lt-page-title">Letter Templates</h2>
          <p className="lt-page-subtitle">
            Manage templates &amp; generate letters for your organisation
          </p>
        </div>
        <button
          className="lt-btn-primary"
          onClick={() => navigate(`/${role}/settings/letter-templates/create`)}
        >
          <Plus size={16} />
          Generate Letter
        </button>
      </div>

      {/* ── Tabs ── */}
      <div className="lt-tabs">
        <button
          className={`lt-tab ${activeTab === "templates" ? "lt-tab--active" : ""}`}
          onClick={() => setActiveTab("templates")}
        >
          Template Editor
        </button>
        <button
          className={`lt-tab ${activeTab === "library" ? "lt-tab--active" : ""}`}
          onClick={() => setActiveTab("library")}
        >
          Letter Library
        </button>
      </div>

      {/* ── Template Editor Tab ── */}
      {activeTab === "templates" &&
        (loading ? (
          <div className="lt-loading">
            <Loader2 className="lt-spinner" />
            <span>Loading templates…</span>
          </div>
        ) : (
          <>
            <Alert className="lt-alert">
              <AlertCircle size={16} />
              <AlertDescription>
                Templates use <code>{"{{placeholders}}"}</code> — they are
                automatically replaced when a letter is generated.
              </AlertDescription>
            </Alert>

            <div className="lt-editor-grid">
              {/* Left sidebar */}
              <div className="lt-sidebar-col">
                <Card className="lt-card">
                  <CardHeader>
                    <CardTitle>Letter Type</CardTitle>
                    <CardDescription>Select a template to edit</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {/* Custom dropdown */}
                    <div className="lt-dropdown-wrap">
                      <button
                        className="lt-dropdown-trigger"
                        onClick={() => setOpenDropdown((v) => !v)}
                      >
                        <span className={selectedType ? "" : "lt-muted"}>
                          {selectedType
                            ? LETTER_TYPES.find((t) => t.value === selectedType)
                                ?.label
                            : "Select Letter Type"}
                        </span>
                        <span className="lt-chevron">▾</span>
                      </button>
                      {openDropdown && (
                        <div className="lt-dropdown-list">
                          {LETTER_TYPES.map((type) => (
                            <div
                              key={type.value}
                              className={`lt-dropdown-item ${selectedType === type.value ? "lt-dropdown-item--active" : ""}`}
                              onClick={() => {
                                setSelectedType(type.value);
                                setOpenDropdown(false);
                              }}
                            >
                              {type.label}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Placeholder chips */}
                    {selectedType && (
                      <div className="lt-placeholder-section">
                        <p className="lt-placeholder-title">
                          Click to insert placeholder
                        </p>
                        <div className="lt-placeholder-grid">
                          {getPlaceholders().map((p) => (
                            <button
                              key={p}
                              className="lt-chip"
                              onClick={() => insertPlaceholder(p)}
                            >
                              {p}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Editor panel */}
              <div className="lt-editor-col">
                <Card className="lt-card">
                  <CardHeader>
                    <div className="lt-editor-header">
                      <div>
                        <CardTitle>Template Editor</CardTitle>
                        <CardDescription>
                          {selectedType
                            ? `Editing: ${LETTER_TYPES.find((t) => t.value === selectedType)?.label}`
                            : "Select a letter type to begin"}
                        </CardDescription>
                      </div>
                      {selectedType && (
                        <div className="lt-editor-actions">
                          <button
                            className="lt-btn-ghost"
                            onClick={handleReset}
                          >
                            Reset to Default
                          </button>
                          {!isEditing && (
                            <button
                              className="lt-btn-ghost"
                              onClick={() => setIsEditing(true)}
                            >
                              <Edit2 size={14} /> Edit
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {selectedType ? (
                      <div className="lt-form">
                        <div className="lt-field">
                          <label className="lt-label">Subject Line</label>
                          <input
                            className="lt-input"
                            value={subject}
                            onChange={(e) => {
                              setSubject(e.target.value);
                              if (!isEditing) setIsEditing(true);
                            }}
                            placeholder="e.g. Job Offer – {{designation}} at {{companyName}}"
                            disabled={!isEditing}
                          />
                        </div>
                        <div className="lt-field">
                          <label className="lt-label">Letter Content</label>
                          <textarea
                            className="lt-textarea"
                            value={content}
                            onChange={(e) => {
                              setContent(e.target.value);
                              if (!isEditing) setIsEditing(true);
                            }}
                            placeholder="Write the letter body here…"
                            rows={22}
                            disabled={!isEditing}
                          />
                        </div>
                        {isEditing && (
                          <div className="lt-save-bar">
                            <button
                              className="lt-btn-ghost"
                              onClick={() => {
                                loadTemplate(selectedType);
                                setIsEditing(false);
                              }}
                            >
                              Cancel
                            </button>
                            <button
                              className="lt-btn-primary"
                              onClick={handleSave}
                              disabled={saving}
                            >
                              {saving ? (
                                <>
                                  <Loader2 size={14} className="lt-spin" />{" "}
                                  Saving…
                                </>
                              ) : (
                                <>
                                  <Save size={14} /> Save Template
                                </>
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="lt-empty-state">
                        <FileText size={48} className="lt-empty-icon" />
                        <p>
                          Select a letter type from the left panel to view and
                          edit its template
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </>
        ))}

      {/* ── Library Tab ── */}
      {activeTab === "library" && <LetterLibrary />}
    </div>
  );
};

export default LetterTemplates;
